/**
 * Contenido de la plantilla. Edita aquí textos, secciones, carta, servicios o campos de formulario: los componentes de
 * src/components/industry se adaptan solos. Las imágenes viven en src/content/website.ts (siteImages): cada sección
 * indica la posición de la imagen que usa (0 = primera). El nombre, el lema, el correo y el teléfono están en siteConfig.ts.
 */
export interface Link { label: string; href: string }

export type HeroVariant = "split" | "editorial" | "fullscreen" | "framed" | "panorama" | "minimal" | "search" | "console" | "collage" | "card" | "statement" | "centered";
export interface HeroSection {
  type: "hero"; id: string; variant: HeroVariant;
  eyebrow: string; title: string; text: string; primary: Link; secondary?: Link;
  images: number[]; stats?: { value: string; label: string }[]; note?: string;
  /** Pies de foto de las imágenes secundarias (portadas «panorama» y «collage»). */
  captions?: string[];
  /** Buscador demostrativo (portada «search»): abre el formulario de contacto con los datos elegidos. */
  search?: { fields: FormField[]; submitLabel: string; target: string };
  /** Tarjeta destacada (portada «card»): horario, cita o datos clave. */
  card?: { title: string; items: string[]; cta: Link };
  /** Muestra una maqueta de la aplicación dibujada con CSS en lugar de una foto (portada «centered»). */
  mock?: { title: string; metrics: { label: string; value: string }[]; rows: string[] };
  /** Sellos que flotan sobre la fotografía (portadas «split» y «console»): opiniones, certificaciones, disponibilidad. */
  chips?: { icon?: IconName; label: string }[];
  /** Ventana de código en lugar de la fotografía (portada «console»), útil para portafolios de desarrollo. */
  code?: { file: string; lines: string[] };
}
export type IconName =
  | "shield"
  | "clock"
  | "spark"
  | "leaf"
  | "chart"
  | "users"
  | "tool"
  | "globe"
  | "heart"
  | "check"
  | "map"
  | "cup"
  | "code"
  | "lock"
  | "bolt"
  | "home"
  | "star"
  | "plane"
  | "activity"
  | "apple"
  | "award"
  | "baby"
  | "badge-check"
  | "bed"
  | "bed-double"
  | "biceps-flexed"
  | "bike"
  | "bot"
  | "boxes"
  | "brain"
  | "briefcase"
  | "building-complex"
  | "calculator"
  | "calendar"
  | "calendar-check"
  | "calendar-clock"
  | "camera"
  | "car"
  | "cat"
  | "clipboard-check"
  | "clipboard-list"
  | "clipboard-pen"
  | "cloud"
  | "coffee"
  | "compass"
  | "concierge-bell"
  | "cpu"
  | "crosshair"
  | "dog"
  | "droplets"
  | "dumbbell"
  | "face-slightly-smiling"
  | "face-slightly-smiling-plus"
  | "file-lock"
  | "flame"
  | "flask-conical"
  | "flower-2"
  | "footprints"
  | "gauge"
  | "gavel"
  | "hand-fist"
  | "hand-heart"
  | "handshake"
  | "hard-hat"
  | "headset"
  | "heart-handshake"
  | "heart-pulse"
  | "hospital"
  | "hourglass"
  | "house"
  | "id-card"
  | "key-round"
  | "lamp"
  | "landmark"
  | "layers"
  | "layout-dashboard"
  | "luggage"
  | "medal"
  | "message-circle-question-mark"
  | "microscope"
  | "moon"
  | "mountain"
  | "music"
  | "paw-print"
  | "person-standing"
  | "pill"
  | "plug"
  | "receipt"
  | "ruler"
  | "sailboat"
  | "salad"
  | "scale"
  | "scan-eye"
  | "scroll-text"
  | "shield-check"
  | "shield-plus"
  | "ship"
  | "shopping-bag"
  | "shower-head"
  | "siren"
  | "sparkles"
  | "sprout"
  | "square-parking"
  | "stethoscope"
  | "sun"
  | "sunrise"
  | "swords"
  | "syringe"
  | "target"
  | "timer"
  | "trending-up"
  | "triangle-alert"
  | "trophy"
  | "user-check"
  | "users-round"
  | "utensils"
  | "utensils-crossed"
  | "waves"
  | "weight"
  | "wheat"
  | "wind"
  | "wine"
  | "workflow"
  | "wrench"
  | "zap";
export interface IndustriesSection { type: "industries"; id: string; eyebrow: string; title: string; items: { name: string; text: string }[] }
export interface CapabilitiesSection {
  type: "capabilities"; id: string; eyebrow: string; title: string; text: string;
  items: { code: string; title: string; text: string; specs: string[] }[];
}
export interface ProcessSection { type: "process"; id: string; eyebrow: string; title: string; image?: number; steps: { title: string; text: string }[] }
export interface GallerySection {
  type: "gallery"; id: string; variant: "facilities" | "portfolio" | "strip" | "mosaic";
  eyebrow: string; title: string; text?: string; items: { image: number; caption: string }[];
}
/**
 * Carrusel de imágenes: una foto grande a la vez, con flechas, puntos, gestos táctiles y teclado.
 * «autoplay» lo avanza solo cada «interval» milisegundos (se pausa al pasar el ratón, al enfocarlo y si el visitante
 * pidió reducir el movimiento). «text» es opcional y se muestra bajo el pie de foto.
 */
export interface CarouselSection {
  type: "carousel"; id: string; eyebrow: string; title: string; text?: string;
  autoplay?: boolean; interval?: number; items: { image: number; caption: string; text?: string }[];
}
export interface CertificationsSection { type: "certifications"; id: string; eyebrow: string; title: string; text: string; items: { name: string; text: string; icon?: IconName }[]; note: string }
export interface ServicesSection {
  type: "services"; id: string; variant: "list" | "cards"; eyebrow: string; title: string; text?: string;
  items: { name: string; text: string; duration?: string; price?: string }[]; note?: string;
}
export interface PackagesSection {
  type: "packages"; id: string; eyebrow: string; title: string; note?: string;
  items: { name: string; price: string; text: string; features: string[]; featured?: boolean }[];
}
export interface TeamSection { type: "team"; id: string; eyebrow: string; title: string; items: { name: string; role: string; text: string }[] }
/** «rating» (1 a 5) dibuja estrellas con iconos; publícalo solo si la calificación es real. */
export interface TestimonialsSection { type: "testimonials"; id: string; eyebrow: string; title: string; items: { quote: string; author: string; detail: string; rating?: number }[]; note: string }
export interface StorySection { type: "story"; id: string; eyebrow: string; title: string; paragraphs: string[]; image: number; signature?: string }
export interface MenuSection {
  type: "menu"; id: string; eyebrow: string; title: string; text?: string; note: string;
  categories: { name: string; items: { name: string; text: string; price: string; tags?: string[] }[] }[];
}
export interface SpecialtiesSection { type: "specialties"; id: string; eyebrow: string; title: string; items: { name: string; text: string; price: string; image: number }[] }
export interface HoursSection { type: "hours"; id: string; eyebrow: string; title: string; text: string }
export interface FormField {
  name: string; label: string; type: "text" | "email" | "tel" | "date" | "time" | "number" | "select" | "textarea";
  required?: boolean; options?: string[]; autocomplete?: string; half?: boolean;
}
export interface FormSection {
  type: "form"; id: string; variant: "quote" | "booking" | "reservation"; eyebrow: string; title: string; text: string;
  submitLabel: string; fields: FormField[]; aside?: string[]; note: string;
}

export interface ListingItem {
  /** «image» es la posición en siteImages; las fichas del servidor de contenido traen su foto en «imageUrl». */
  title: string; image: number; imageUrl?: string; imageAlt?: string; text: string; category?: string; location?: string; price?: string; priceNote?: string;
  meta: string[]; badge?: string; details?: { description: string; features: string[] };
  /** Enlace externo (demo, repositorio); con «href» vacío no se muestra. */
  link?: Link;
}
/** Fichas (habitaciones, destinos, propiedades, proyectos o productos) con filtro opcional y ficha detallada en un diálogo. */
export interface ListingSection {
  type: "listing"; id: string; variant: "rooms" | "tours" | "properties" | "projects" | "products";
  eyebrow: string; title: string; text?: string; filterLabel?: string; categories?: string[]; items: ListingItem[]; note?: string; cta?: Link;
}
export interface FeaturesSection { type: "features"; id: string; variant: "icons" | "list" | "cards"; eyebrow: string; title: string; text?: string; items: { icon?: IconName; title: string; text: string }[] }
export interface StatsSection { type: "stats"; id: string; eyebrow?: string; title?: string; items: { value: string; label: string }[]; note?: string }
export interface FaqSection { type: "faq"; id: string; eyebrow: string; title: string; items: { question: string; answer: string }[] }
export interface TimelineSection { type: "timeline"; id: string; eyebrow: string; title: string; items: { year: string; title: string; text: string }[] }
export interface LogosSection { type: "logos"; id: string; title: string; items: string[]; note?: string }
export interface CtaSection { type: "cta"; id: string; eyebrow?: string; title: string; text: string; primary: Link; secondary?: Link; image?: number }

export type Section = HeroSection | ListingSection | FeaturesSection | StatsSection | FaqSection | TimelineSection | LogosSection | CtaSection | IndustriesSection | CapabilitiesSection | ProcessSection | GallerySection | CarouselSection | CertificationsSection
  | ServicesSection | PackagesSection | TeamSection | TestimonialsSection | StorySection | MenuSection | SpecialtiesSection | HoursSection | FormSection;

export interface IndustryContent {
  id: string;
  /** Estructura (arquetipo) y dirección de arte: cambian la composición en styles/industry.css. */
  archetype: string;
  style: string;
  /** Estilo de diseño elegido en WebForge (minimal, neobrutalism, swiss, editorial, retro, bento…); capa de styles/looks.css. */
  look?: string;
  header: { variant: "compact" | "centered" | "transparent" | "minimal" | "stacked" | "floating"; topbar: string[]; cta: Link };
  nav: Link[];
  /** Quita, reordena o duplica secciones: la página las dibuja en este orden. */
  sections: Section[];
  contact: { address: string; hours: { days: string; time: string }[]; mapUrl: string; social: Link[] };
  footer: { about: string; legal: string };
  disclaimer: string;
  /** Botón flotante de WhatsApp (usa el teléfono de siteConfig.ts). Pon «enabled: false» para ocultarlo. */
  whatsapp?: { enabled?: boolean; message?: string; label?: string };
}

export const industry: IndustryContent = {
  "id": "gimnasio-forja-iron-club",
  "archetype": "immersive-fullscreen",
  "style": "iron-volt",
  "header": {
    "variant": "transparent",
    "topbar": [],
    "cta": {
      "label": "Clase de prueba",
      "href": "#trial"
    }
  },
  "nav": [
    {
      "label": "Programas",
      "href": "#programs"
    },
    {
      "label": "Horario",
      "href": "#schedule"
    },
    {
      "label": "Membresías",
      "href": "#memberships"
    },
    {
      "label": "Coaches",
      "href": "#coaches"
    },
    {
      "label": "Visítanos",
      "href": "#visit"
    }
  ],
  "sections": [
    {
      "type": "hero",
      "id": "hero",
      "variant": "fullscreen",
      "eyebrow": "Fuerza · Powerlifting · Guadalajara",
      "title": "Más fuerte cada semana. Sin atajos.",
      "text": "Plataformas olímpicas, discos calibrados y coaches que corrigen cada repetición. Entrena con un plan, mide tu progreso y levanta más de lo que creías posible.",
      "primary": {
        "label": "Agenda tu clase de prueba",
        "href": "#trial"
      },
      "secondary": {
        "label": "Ver membresías",
        "href": "#memberships"
      },
      "images": [
        0
      ],
      "note": "Abierto de lunes a viernes de 5:00 a 23:00 · Sábados y domingos con horario reducido"
    },
    {
      "type": "stats",
      "id": "numbers",
      "items": [
        {
          "value": "12",
          "label": "Plataformas de levantamiento"
        },
        {
          "value": "1,800 m²",
          "label": "De piso de entrenamiento"
        },
        {
          "value": "10",
          "label": "Personas máximo por clase"
        },
        {
          "value": "18 h",
          "label": "Abierto entre semana"
        }
      ],
      "note": "Cifras de ejemplo: cámbialas por las de tu gimnasio."
    },
    {
      "type": "services",
      "id": "programs",
      "variant": "cards",
      "eyebrow": "Programas",
      "title": "Elige cómo quieres hacerte fuerte",
      "text": "Todos los programas empiezan con una evaluación de movilidad y técnica para fijar tus cargas de inicio.",
      "items": [
        {
          "name": "Fuerza 101",
          "text": "Aprende sentadilla, press de banca y peso muerto con técnica segura y progresiones semanales.",
          "duration": "8 semanas",
          "price": "Incluido"
        },
        {
          "name": "Powerlifting",
          "text": "Bloques de fuerza máxima, picos de rendimiento y preparación para tu primera competencia.",
          "duration": "Ciclos de 12 semanas",
          "price": "Incluido"
        },
        {
          "name": "Hipertrofia",
          "text": "Volumen bien dosificado, accesorios y control de cargas para ganar músculo sin estancarte.",
          "duration": "4 días por semana",
          "price": "Incluido"
        },
        {
          "name": "Fuerza 50+",
          "text": "Fuerza, equilibrio y salud ósea con cargas adaptadas y grupos reducidos.",
          "duration": "3 días por semana",
          "price": "Incluido"
        },
        {
          "name": "Coaching 1 a 1",
          "text": "Programa personal, revisión de video y sesiones privadas con tu coach.",
          "duration": "Sesiones de 60 min",
          "price": "Desde $650"
        },
        {
          "name": "Open gym",
          "text": "Usa plataformas, racks y mancuernas a tu ritmo con un coach de piso disponible.",
          "duration": "Todo el horario",
          "price": "Incluido"
        }
      ],
      "note": "Precios de ejemplo en pesos mexicanos."
    },
    {
      "type": "gallery",
      "id": "floor",
      "variant": "mosaic",
      "eyebrow": "El piso",
      "title": "Equipo de competencia, sin filas",
      "text": "Racks de potencia, barras con buen agarre y discos calibrados para que el número en la barra sea el real.",
      "items": [
        {
          "image": 3,
          "caption": "Racks de potencia y plataformas"
        },
        {
          "image": 4,
          "caption": "Mancuernas hasta 60 kg"
        },
        {
          "image": 1,
          "caption": "Zona de peso muerto"
        }
      ]
    },
    {
      "type": "menu",
      "id": "schedule",
      "eyebrow": "Horario de clases",
      "title": "Encuentra tu hora",
      "text": "Cupo de 10 personas por clase para que el coach vea cada repetición.",
      "note": "Reserva tu lugar desde la app o en recepción. Horario de ejemplo.",
      "categories": [
        {
          "name": "Lun · Mié · Vie",
          "items": [
            {
              "name": "Fuerza 101 · mañana",
              "text": "Técnica y progresión de los tres básicos.",
              "price": "06:00",
              "tags": [
                "Principiante"
              ]
            },
            {
              "name": "Powerlifting · fuerza máxima",
              "text": "Series pesadas y trabajo cerca de tu máximo.",
              "price": "07:30",
              "tags": [
                "Avanzado"
              ]
            },
            {
              "name": "Hipertrofia",
              "text": "Tren inferior y accesorios.",
              "price": "13:00",
              "tags": [
                "Intermedio"
              ]
            },
            {
              "name": "Fuerza 50+",
              "text": "Fuerza, equilibrio y movilidad.",
              "price": "17:00",
              "tags": [
                "Todos los niveles"
              ]
            },
            {
              "name": "Powerlifting · volumen",
              "text": "Bloque de volumen técnico.",
              "price": "19:00",
              "tags": [
                "Avanzado"
              ]
            },
            {
              "name": "Fuerza 101 · noche",
              "text": "Grupo vespertino.",
              "price": "20:30",
              "tags": [
                "Principiante"
              ]
            }
          ]
        },
        {
          "name": "Mar · Jue",
          "items": [
            {
              "name": "Hipertrofia · tren superior",
              "text": "Pecho, hombro y espalda.",
              "price": "06:00",
              "tags": [
                "Intermedio"
              ]
            },
            {
              "name": "Movilidad para levantar",
              "text": "Cadera, tobillo y hombro para mejores posiciones.",
              "price": "08:00",
              "tags": [
                "Todos los niveles"
              ]
            },
            {
              "name": "Powerlifting",
              "text": "Press de banca y variantes.",
              "price": "18:00",
              "tags": [
                "Avanzado"
              ]
            },
            {
              "name": "Hipertrofia · noche",
              "text": "Grupo vespertino de tren superior.",
              "price": "20:00",
              "tags": [
                "Intermedio"
              ]
            }
          ]
        },
        {
          "name": "Sábado",
          "items": [
            {
              "name": "Día de récords",
              "text": "Pruebas de 1RM con jueces y música alta.",
              "price": "09:00",
              "tags": [
                "Comunidad"
              ]
            },
            {
              "name": "Clínica de técnica",
              "text": "Un movimiento a fondo cada semana.",
              "price": "11:00",
              "tags": [
                "Todos los niveles"
              ]
            }
          ]
        }
      ]
    },
    {
      "type": "packages",
      "id": "memberships",
      "eyebrow": "Membresías",
      "title": "Sin inscripción. Sin letras chiquitas.",
      "items": [
        {
          "name": "Open gym",
          "price": "$790 / mes",
          "text": "Para quien ya tiene su plan y solo necesita buen equipo.",
          "features": [
            "Acceso todo el horario",
            "Coach de piso",
            "Lockers y regaderas"
          ]
        },
        {
          "name": "Fuerza guiada",
          "price": "$1,290 / mes",
          "text": "Clases ilimitadas con programación y seguimiento.",
          "features": [
            "Todas las clases del horario",
            "Evaluación trimestral de fuerza",
            "Programa en la app",
            "Open gym incluido"
          ],
          "featured": true
        },
        {
          "name": "Coaching 1 a 1",
          "price": "$3,400 / mes",
          "text": "Tu programa, tu coach y revisión de cada sesión.",
          "features": [
            "8 sesiones privadas",
            "Revisión de video semanal",
            "Plan de nutrición básico",
            "Acceso total al gimnasio"
          ]
        }
      ],
      "note": "Precios de ejemplo. Puedes congelar tu membresía hasta 30 días al año."
    },
    {
      "type": "team",
      "id": "coaches",
      "eyebrow": "Coaches",
      "title": "Gente que levanta y sabe enseñar",
      "items": [
        {
          "name": "Daniela Ríos",
          "role": "Head coach · Powerlifting",
          "text": "Agrega aquí sus certificaciones y marcas personales verificables."
        },
        {
          "name": "Marco Villaseñor",
          "role": "Fuerza e hipertrofia",
          "text": "Agrega aquí sus certificaciones y su experiencia."
        },
        {
          "name": "Lucía Gómez",
          "role": "Movilidad y Fuerza 50+",
          "text": "Agrega aquí su formación y especialidad."
        }
      ]
    },
    {
      "type": "testimonials",
      "id": "results",
      "eyebrow": "Resultados",
      "title": "Lo que dicen en el rack",
      "items": [
        {
          "quote": "Llegué sin saber hacer una sentadilla. Seis meses después competí por primera vez y saqué 120 kg.",
          "author": "Andrea M.",
          "detail": "Programa de Powerlifting",
          "rating": 5
        },
        {
          "quote": "Por fin un gimnasio donde nadie acapara el rack y alguien te corrige la técnica.",
          "author": "Jorge L.",
          "detail": "Open gym",
          "rating": 5
        },
        {
          "quote": "A mis 58 años me siento más fuerte que a los 40. Las clases 50+ me cambiaron el día a día.",
          "author": "Rosa P.",
          "detail": "Fuerza 50+",
          "rating": 5
        }
      ],
      "note": "Testimonios de ejemplo: publica solo opiniones reales y con permiso de sus autores."
    },
    {
      "type": "cta",
      "id": "first-week",
      "eyebrow": "Primera semana",
      "title": "Ven a levantar con nosotros",
      "text": "Tu clase de prueba incluye evaluación de técnica y un plan de inicio para tus primeras cuatro semanas.",
      "primary": {
        "label": "Agendar clase de prueba",
        "href": "#trial"
      },
      "secondary": {
        "label": "Ver horario",
        "href": "#schedule"
      },
      "image": 6
    },
    {
      "type": "faq",
      "id": "faq",
      "eyebrow": "Preguntas frecuentes",
      "title": "Antes de tu primera visita",
      "items": [
        {
          "question": "¿Necesito experiencia para entrar?",
          "answer": "No. Fuerza 101 está pensado para empezar desde cero y aprender la técnica antes de subir cargas."
        },
        {
          "question": "¿Qué llevo a mi clase de prueba?",
          "answer": "Ropa cómoda, tenis de suela plana, toalla y agua. Nosotros ponemos el equipo, el magnesio y los cinturones."
        },
        {
          "question": "¿Hay permanencia mínima?",
          "answer": "No. Las membresías son mensuales y puedes cancelar con aviso de 7 días."
        },
        {
          "question": "¿Tienen estacionamiento y regaderas?",
          "answer": "Sí: estacionamiento para socios, lockers, regaderas con agua caliente y área de vestidores."
        }
      ]
    },
    {
      "type": "hours",
      "id": "visit",
      "eyebrow": "Visítanos",
      "title": "Abierto temprano, cerramos tarde",
      "text": "Estacionamiento para socios y acceso con código desde la app."
    },
    {
      "type": "form",
      "id": "trial",
      "variant": "booking",
      "eyebrow": "Clase de prueba",
      "title": "Agenda tu primera sesión sin costo",
      "text": "Elige día y horario. Un coach te contacta para confirmar y preparar tu evaluación.",
      "submitLabel": "Agendar clase de prueba",
      "aside": [
        "Evaluación de técnica y movilidad",
        "Plan de inicio para cuatro semanas",
        "Sin compromiso de inscripción"
      ],
      "fields": [
        {
          "name": "nombre",
          "label": "Nombre completo",
          "type": "text",
          "required": true,
          "autocomplete": "name",
          "half": true
        },
        {
          "name": "telefono",
          "label": "Teléfono",
          "type": "tel",
          "required": true,
          "autocomplete": "tel",
          "half": true
        },
        {
          "name": "correo",
          "label": "Correo electrónico",
          "type": "email",
          "required": true,
          "autocomplete": "email",
          "half": true
        },
        {
          "name": "fecha",
          "label": "Día preferido",
          "type": "date",
          "required": true,
          "half": true
        },
        {
          "name": "experiencia",
          "label": "Experiencia con pesas",
          "type": "select",
          "options": [
            "Ninguna",
            "Menos de un año",
            "De uno a tres años",
            "Más de tres años"
          ],
          "half": true
        },
        {
          "name": "horario",
          "label": "Horario",
          "type": "select",
          "options": [
            "Mañana",
            "Mediodía",
            "Tarde",
            "Noche"
          ],
          "half": true
        },
        {
          "name": "objetivo",
          "label": "¿Qué te gustaría lograr?",
          "type": "textarea"
        }
      ],
      "note": "El formulario abre el correo del visitante con los datos; conéctalo a tu sistema de reservas cuando lo publiques."
    }
  ],
  "contact": {
    "address": "Av. Industria 455, Col. Americana, Guadalajara, Jal.",
    "hours": [
      {
        "days": "Lunes a viernes",
        "time": "05:00 – 23:00"
      },
      {
        "days": "Sábado",
        "time": "07:00 – 20:00"
      },
      {
        "days": "Domingo",
        "time": "08:00 – 14:00"
      }
    ],
    "mapUrl": "https://www.openstreetmap.org/search?query=Guadalajara%20Jalisco",
    "social": [
      {
        "label": "Instagram",
        "href": "https://www.instagram.com/"
      },
      {
        "label": "TikTok",
        "href": "https://www.tiktok.com/"
      },
      {
        "label": "YouTube",
        "href": "https://www.youtube.com/"
      }
    ]
  },
  "footer": {
    "about": "Gimnasio de fuerza con plataformas olímpicas, coaches de powerlifting y programas para todos los niveles.",
    "legal": "Contenido de ejemplo generado con WebForge Studio. Consulta a un médico antes de iniciar un programa de ejercicio."
  },
  "whatsapp": {
    "message": "Hola, Forja Iron Club. Quiero agendar mi clase de prueba.",
    "label": "Escríbenos por WhatsApp"
  },
  "disclaimer": "Gimnasio ficticio. Nombres, cifras, precios y testimonios son de ejemplo; no se publican certificaciones ni marcas personales.",
  "look": "original"
};
